<script lang="ts">
  import Separator from '@/lib/components/ui/separator/separator.svelte';
  import XataContent from '../XataContent.svelte';
  import XssDeficitGauge from '../XssDeficitGauge.svelte';
  import HelpTooltip from '@/Common/HelpTooltip.svelte';
  import { ImprovementRateText, type ImprovementRate } from '@/Common/constants';
  import type { MyFitnessStores } from '../stores';

  export let myFitness: MyFitnessStores;

  let { trainingAdvice } = myFitness;

  let programType
  let programWarningMessage: string | null = null;
  let programActive;
  $: isContinuous = programType == "continuous";

  $: if ($trainingAdvice) {
    programType = $trainingAdvice.program.type;
    programWarningMessage = null;
    if ($trainingAdvice.phase.startsWith("Pre Base")) {
      programWarningMessage = 'Your Training Program has not started.';
      programActive = false;
    } else if ($trainingAdvice.phase.startsWith("Post Event")) {
      programWarningMessage = 'Your Training Program has ended.';
      programActive = false;
    } else {
      programActive = true;
      if ($trainingAdvice.phase.startsWith("Taper Week") && $trainingAdvice.training_gradient > 0) {
        programWarningMessage = 'You are in your Taper Week and Improvement Rate is currently set too high. Reduce to Maintenance or lower.';
      }
    }
  }

  // Last 6 weeks of ramp rate (one value per week), already embedded on the advice for
  // continuous plans (see UserController::getAnyProgramStatistics / _getContinuousProgramStatistics),
  // the same data CurrentProgram's slider uses.
  $: sixWeekAvgImprovementRate = isContinuous && $trainingAdvice?.program?.data?.length
    ? $trainingAdvice.program.data.reduce((sum: number, v: number) => sum + v, 0) / $trainingAdvice.program.data.length
    : null;

  // Shown on the same named scale as the Improvement Rate setting, rather than as a raw number.
  function improvementRateLabel(avg: number | null): string {
    if (avg == null) return '--';
    if (avg < -2) return 'Detraining';
    const bucket = Math.max(-2, Math.min(7, Math.round(avg))) as ImprovementRate;
    return String(ImprovementRateText[bucket] ?? '--');
  }
  $: sixWeekAvgLabel = improvementRateLabel(sixWeekAvgImprovementRate);

  // Smaller than HelpTooltip's default trigger so this tightly packed row list doesn't
  // stretch its line height out to button size.
  const infoIconClass = "w-5 h-5 text-gray-500";
</script>

<div class='flex flex-col gap-1 items-center'>
  <div class='mb-2 w-full max-w-[280px]'>
    {#if $trainingAdvice}
      <div class="subtext deficit-guide leading-tight space-y-1">
        {#if isContinuous}
          <div>Program
            <HelpTooltip message="Your training program is set to <strong>Continuous Improvement</strong> &mdash; steadily building fitness over time with no fixed end date. <a href='http://www.baronbiosys.com/glossary/program-type' target='_help'>Learn more</a>" iconClass={infoIconClass} />
            : <strong>Continuous Improvement</strong></div>

          <div>Improvement Rate
            <HelpTooltip message="How aggressively your program increases your training load week to week. Higher settings build fitness faster but require more recovery. <a href='https://baronbiosys.com/glossary/improvement-rate' target='_help'>Learn more</a>" iconClass={infoIconClass} />
            : <strong>{$trainingAdvice.training_grad_val?.text}</strong></div>

          <div>Last 6 Weeks
            <HelpTooltip message="The Improvement Rate you have averaged over the past 6 weeks. <a href='https://baronbiosys.com/glossary/improvement-rate' target='_help'>Learn more</a>" iconClass={infoIconClass} />
            : <strong>{sixWeekAvgLabel}</strong></div>
        {:else if programType == "challenge"}
          <div>Program
            <HelpTooltip message="You're running a <strong>30 Day Challenge</strong> &mdash; a short, focused block of training. <a href='http://www.baronbiosys.com/glossary/program-type' target='_help'>Learn more</a>" iconClass={infoIconClass} />
            : <strong>30 Day Challenge</strong></div>
        {:else}
          <div>Program
            <HelpTooltip message="You're training toward a target event, currently in the <strong>{$trainingAdvice.phase}</strong> phase. <a href='http://www.baronbiosys.com/glossary/program-phases' target='_help'>Learn more</a>" iconClass={infoIconClass} />
            : Target Event - <strong>{$trainingAdvice.phase}</strong> Phase
            {#if programWarningMessage}
              <HelpTooltip message={programWarningMessage} icon="fa fa-warning" iconClass="w-5 h-5 text-amber-500" />
            {/if}
          </div>
        {/if}

        {#if !(programActive && programType == "targetEvent") || ($trainingAdvice.recommended_athlete == "Endurance") || ($trainingAdvice.recommended_athlete == "Recovery")}
          <div>Focus Type
            <HelpTooltip message="The type of training that will best help you reach your goal right now. <a href='http://baronbiosys.com/understanding-focus-and-how-to-use-it/' target='_help'>Learn more</a>" iconClass={infoIconClass} />
            : <strong>{$trainingAdvice.recommended_athlete}</strong></div>
        {:else}
          <div>Focus Type
            <HelpTooltip message="The type of training that will best help you reach your goal right now. <a href='http://baronbiosys.com/understanding-focus-and-how-to-use-it/' target='_help'>Learn more</a>" iconClass={infoIconClass} />
            : <strong>{$trainingAdvice.recommended_spec_rating} {$trainingAdvice.recommended_athlete}</strong></div>
        {/if}
        <div>Workout Difficulty to Choose
          <HelpTooltip message="Maximum difficulty of the workout you should choose based on your current training status. <a href='https://www.baronbiosys.com/glossary/difficulty-rating/' target='_help'>Learn more</a>" iconClass={infoIconClass} />
          : <strong><span class="diamond">{@html $trainingAdvice.difficulty_rating}</span></strong></div>
      </div>
    {/if}
  </div>

  <div class="w-full max-w-[280px] flex flex-col items-center">
    <XssDeficitGauge training_advice={$trainingAdvice} />
    {#if $trainingAdvice}
      <div class="subtext text-center">
        Training {$trainingAdvice.xss_deficit > 0 ? 'Deficit' : 'Surplus'}
        <HelpTooltip message="The gap between the training you've completed and what your program calls for &mdash; a deficit means you're behind pace, a surplus means you're ahead. <a href='https://baronbiosys.com/glossary/training-deficits-and-surpluses' target='_help'>Learn more</a>" />
        : <strong>{Math.abs($trainingAdvice.xss_deficit).toFixed(0)} XSS</strong>
        {#if $trainingAdvice.progress_indicator < -0.4}
          <HelpTooltip
            message="You have fallen behind. Increase your training volume or reduce your Improvement Rate. <a href='https://baronbiosys.com/glossary/training-deficits-and-surpluses' target='_help'>Learn more</a>"
            icon="fa fa-warning"
            iconClass="text-amber-500"
          />
        {/if}
      </div>
    {/if}
  </div>
</div>
<Separator />
<XataContent trainingAdvice={$trainingAdvice} hideSecondParagraph />
